import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { parseProfileSidebarParams } from 'utils/profileSidebarParams';
import { isAgentChatOpen } from './agentChatParam';

type Params = {
  /** The assistant feature gate — no restores while it's off. */
  enabled: boolean;
  /**
   * Called when a navigation dropped the panel's param. The caller re-applies
   * it (openAgentChat) and marks the reopen as a restore rather than a fresh
   * user open (see the suppress ref in AgentChatSidebar).
   */
  onRestore: () => void;
};

/**
 * Keeps the assistant panel open across app transitions.
 *
 * The `agent` query param is the panel's canonical state, but nav links and
 * MFE-built URLs assemble their destination from scratch, so navigating to
 * another app would drop the param and silently close the panel. Treat the
 * param as shell-owned instead: when the previous location had the panel open
 * and a navigation that CHANGED the pathname lost the param, the caller
 * re-applies it in place (a replace — the navigation already made the history
 * entry).
 *
 * Explicit closes are unaffected: closeAgentChat and the yield paths (the
 * composer or an MFE sider taking the slot) rewrite the URL on the SAME
 * pathname, so no restore fires. The trade-off is that a same-pathname URL
 * rewrite that rebuilds the query from scratch would read as a close — but
 * cross-app writers go through `replaceSharedUrl`-style rebuilds that
 * preserve foreign params, so in practice only real closes look like closes.
 */
export const useRestoreAgentChatOnNavigation = ({
  enabled,
  onRestore,
}: Params): void => {
  const { pathname, search } = useLocation();
  const isOpen = isAgentChatOpen(search);

  // Latest-callback ref so the effect below doesn't re-run (and mis-track the
  // previous location) when the caller's callback identity changes.
  const onRestoreRef = useRef(onRestore);
  useEffect(() => {
    onRestoreRef.current = onRestore;
  });

  // A navigation that carries an open people profile is a newer claim on the
  // right-hand slot (a profile deep link) — restoring would strip it, since
  // opening the assistant closes the profile (last-opened-wins). Yield to it.
  const profileOpen = parseProfileSidebarParams(search).userId !== null;

  const prevRef = useRef({ pathname, wasOpen: isOpen });
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = { pathname, wasOpen: isOpen };
    if (
      enabled &&
      prev.wasOpen &&
      !isOpen &&
      !profileOpen &&
      pathname !== prev.pathname
    ) {
      onRestoreRef.current();
    }
  }, [enabled, pathname, isOpen, profileOpen]);
};
